/**
 * The JUCO toggle on roster import.
 *
 * A coach photographing a junior-college roster flips one switch and every player on that team is
 * stored as a JUCO player, at the JC year the roster's Class column named. Reading the cell is
 * already solved — `readRosterClass` returns both ladders off one read — so what lives here is
 * the bookkeeping around it: which rows the toggle could not read, what the coach hand-filled,
 * and how the two fields the toggle owns reach the wire.
 *
 * SCOPED PER TEAM, NEVER PER BATCH. A showcase upload is many teams, and a prep program with a
 * calendar-year Grad column sits inside the same scout book as the JUCO rosters (TCS, p35 of the
 * book Patrick sent). A batch-level toggle would stamp those players JUCO, so the toggle and its
 * edits are keyed by team and nothing in this module knows a batch exists.
 *
 * THE TOGGLE HAS TO WIN OVER STORED DATA. The API only overwrites fields a row names in
 * `overwriteFields`, so a row stamped by the toggle names both of them — see
 * {@link JUCO_OVERWRITE_FIELDS}. Without that a re-import of the same roster would leave a stale
 * `HighSchool` in place and the toggle would look like it did nothing.
 *
 * Mirrors `mobile/utils/juco-import.ts`. Keep the two in step.
 */

import type { ExtractedRow, ShowcaseProspectRequest } from '../../../../types/extraction';
import { JUCO_PLAYER_LEVEL } from '../../../../constants/playerLevels';
import {
  JUCO_YEARS,
  formatJucoYear,
  jucoGraduationYearFor,
  readRosterClass,
  type JucoYear,
} from '../../../../utils/roster-class';

/**
 * The fields the JUCO toggle owns, as the camelCase names the API accepts in `overwriteFields`.
 *
 * Mirrors `ProspectFieldHelper.RosterClassificationFields` on the API, which is the one place a
 * roster import may overwrite stored data however the row was matched. Both names must be sent:
 * naming only `playerLevel` would leave a JC year from an earlier import contradicting the level.
 */
export const JUCO_OVERWRITE_FIELDS: readonly string[] = ['playerLevel', 'jucoYear'];

/** How a JC year reads in a picker: `JC 1`, `JC 2`, `JC 3`. */
export const JUCO_YEAR_LABELS: readonly string[] = JUCO_YEARS.map(
  (year) => formatJucoYear(year) ?? `JC ${year}`,
);

/** How one JC year reads in a picker, or `''` when there is no year to show. */
export function jucoYearLabel(year: JucoYear | null | undefined): string {
  return year ? (formatJucoYear(year) ?? '') : '';
}

/** The year behind a picker label, or null when the label names none (including the reset row). */
export function jucoYearFromLabel(label: string | null | undefined): JucoYear | null {
  if (!label) return null;
  const index = JUCO_YEAR_LABELS.indexOf(label);
  return index === -1 ? null : JUCO_YEARS[index];
}

/**
 * JC years the coach filled in by hand, keyed by index into the team's row list.
 *
 * Kept separate from the parsed reading rather than folded into it, so flipping the toggle off
 * and on again does not silently discard corrections, and so a row whose cell the parser CAN read
 * is still overridable.
 */
export type JucoYearEdits = Record<number, JucoYear>;

/** One roster row as the JC-year review lists it. */
export interface JucoReviewRow {
  /** Index into the team's rows — the key for edits, and the join back to the outgoing payload. */
  index: number;
  /** How the row reads in the list: "12 Sam Diaz", or "Row 4" when the row has no name at all. */
  label: string;
  /** What the Class cell said, shown so a coach can see why a row could not be read. */
  rawClass: string;
  /** The JC year the cell named, or null when it named none ("Jr", a blank column, a grad year). */
  parsedJucoYear: JucoYear | null;
}

/** Read a cell as trimmed text. Numbers arrive here too — a Class column of bare years does. */
function cellText(row: ExtractedRow, key: string): string {
  const value = row[key];
  return value == null ? '' : String(value).trim();
}

/** Whether a row carries enough of a name to be imported at all. */
export function hasImportableName(row: ExtractedRow): boolean {
  return cellText(row, 'first_name').length > 0 && cellText(row, 'last_name').length > 0;
}

/**
 * The rows the JC-year review shows, in roster order.
 *
 * `skipUnnamedRows` follows whichever filter the caller's own payload builder applies: the
 * multi-team path drops rows with no usable name before it sends anything, so those rows must not
 * be listed and must not be able to block a confirm. The single-team path imports every row
 * (names default to "Unknown"), so it lists every row.
 */
export function buildJucoReviewRows(
  rows: ExtractedRow[],
  options: { skipUnnamedRows: boolean },
): JucoReviewRow[] {
  const reviewRows: JucoReviewRow[] = [];

  rows.forEach((row, index) => {
    if (options.skipUnnamedRows && !hasImportableName(row)) return;

    const rawClass = cellText(row, 'class') || cellText(row, 'grad_year');
    const jersey = cellText(row, 'number');
    const name = `${cellText(row, 'first_name')} ${cellText(row, 'last_name')}`.trim();

    reviewRows.push({
      index,
      label: [jersey && `#${jersey}`, name].filter(Boolean).join(' ') || `Row ${index + 1}`,
      rawClass,
      // One read of the cell, exactly as the payload builders read it, so the review and the
      // outgoing row can never disagree about what the roster said.
      parsedJucoYear: readRosterClass(row.class || row.grad_year).jucoYear,
    });
  });

  return reviewRows;
}

/** The JC year a row will import with: the coach's correction, else what the cell said. */
export function jucoYearForRow(row: JucoReviewRow, edits: JucoYearEdits): JucoYear | null {
  return edits[row.index] ?? row.parsedJucoYear;
}

/**
 * Rows a JUCO-marked team cannot import yet, because neither the roster nor the coach has said
 * which year of junior college they are in.
 *
 * A JUCO player with no JC year is the thing this feature exists to prevent, so confirm is
 * blocked on this being empty rather than the row being sent half-stamped.
 */
export function unresolvedJucoRows(
  rows: JucoReviewRow[],
  edits: JucoYearEdits,
): JucoReviewRow[] {
  return rows.filter((row) => jucoYearForRow(row, edits) === null);
}

/**
 * Fill every still-unresolved row with one year.
 *
 * Not a convenience. Two of the 41 pages in the scout book (Alvin CC p5, Vernon College p38) have
 * no class column at all, so flipping the toggle on one of them leaves every row unresolved; with
 * no way to answer in bulk the import is a dead end the coach cannot get out of. Rows that already
 * have a year — parsed or hand-filled — are left alone, so this cannot overwrite a reading.
 */
export function fillBlankJucoYears(
  rows: JucoReviewRow[],
  edits: JucoYearEdits,
  year: JucoYear,
): JucoYearEdits {
  const filled: JucoYearEdits = { ...edits };
  for (const row of unresolvedJucoRows(rows, edits)) {
    filled[row.index] = year;
  }
  return filled;
}

/** The JC year each row imports with, keyed by row index. */
export function jucoYearsByRowIndex(
  rows: JucoReviewRow[],
  edits: JucoYearEdits,
): Map<number, JucoYear> {
  const years = new Map<number, JucoYear>();
  for (const row of rows) {
    const year = jucoYearForRow(row, edits);
    if (year) years.set(row.index, year);
  }
  return years;
}

/**
 * Union of `overwriteFields` lists, order preserved, duplicates dropped.
 *
 * The duplicate-import guard hands back its own list per row, and a JUCO-stamped row already
 * carries two names of its own. Whichever assignment ran last used to win; both have to survive.
 */
export function unionOverwriteFields(
  ...lists: (readonly string[] | undefined)[]
): string[] {
  const union = new Set<string>();
  for (const list of lists) {
    for (const field of list ?? []) union.add(field);
  }
  return Array.from(union);
}

/**
 * Stamp one outgoing row as a JUCO player.
 *
 * `playerLevel` is the ordinal, and JUCO's ordinal is 1 — but High School's is `0`, which is
 * falsy, so nothing downstream may test this field for truthiness. `jucoYear` is omitted rather
 * than sent as null when unknown: the DTO's regex rejects anything outside 1-3, and confirm is
 * blocked before a JUCO-marked row can get here without one.
 *
 * THE GRAD YEAR IS RE-INFERRED ON THE TWO-YEAR LADDER. `readRosterClass` derives its grad year
 * from the four-year eligibility ladder, which puts a JUCO freshman three classes out — wrong
 * twice over, since they are not on a four-year track and the number a coach wants is when the
 * player can arrive. Left alone, a JC 1 imported as the class of 2030 instead of 2028, and
 * nothing downstream would ever correct it: `gradYear` is deliberately not in
 * {@link JUCO_OVERWRITE_FIELDS}, and the grad-year facet, the sort and the exports all read it.
 *
 * `gradYearWasStated` is the one case this must not touch. A cell holding `2025` is a fact the
 * roster carried, not an inference — the prep program sitting inside the JUCO scout book lists
 * exactly that — so a stated year always wins over either ladder.
 */
export function stampJucoOnPlayer(
  player: ShowcaseProspectRequest,
  jucoYear: JucoYear | null,
  options: { gradYearWasStated: boolean } = { gradYearWasStated: false },
): ShowcaseProspectRequest {
  const jucoGradYear =
    jucoYear && !options.gradYearWasStated ? jucoGraduationYearFor(jucoYear) : null;

  return {
    ...player,
    playerLevel: JUCO_PLAYER_LEVEL,
    ...(jucoYear ? { jucoYear } : {}),
    ...(jucoGradYear != null ? { gradYear: jucoGradYear } : {}),
    overwriteFields: unionOverwriteFields(player.overwriteFields, JUCO_OVERWRITE_FIELDS),
  };
}
